import { ErrorHandler, inject, Injector, Service } from '@angular/core';
import { DialogService, ToastService } from '@h5s/design-system';
import { AppError, ErrorPresentation } from '@h5s/util-error';
import { ERROR_DIALOG_TRANSLATIONS, ErrorService } from './error.service';
import { isLogoutInProgressError } from './models/logout-in-progress-error';

@Service({ autoProvided: false })
export class GlobalErrorHandler implements ErrorHandler {
  private readonly errorService = inject(ErrorService);
  private readonly injector = inject(Injector);

  private toastService: ToastService;
  private dialogService: DialogService;

  handleError(error: AppError): void {
    // Silenziamo il caso in cui l'utente sta facendo logout
    if (isLogoutInProgressError(error)) {
      return;
    }

    console.error('Global Error Handler: ', error);

    // Un AppError è considerato "user facingOrValidationError" se lui stesso, o una qualsiasi
    // delle sue cause (risalendo ricorsivamente la catena), ha l'info
    // 'isUserFacingOrValidationError' impostata.
    const isUserFacingOrValidationError = error instanceof AppError && error.hasUserFacingOrValidationErrorCause();

    // Iniettiamo ToastService manualmente tramite injector.get()
    // perché al suo interno dipende da ApplicationRef.
    //
    // Se provassimo ad iniettarlo direttamente nel costruttore, Angular solleverebbe un errore
    // RuntimeError: NG0200: Circular dependency in DI detected for ApplicationRef.
    //
    // Questo accade perché ApplicationRef è una dipendenza inizializzata solo dopo
    // un certo punto del ciclo di avvio dell'app.
    if (!this.toastService) {
      this.toastService = this.injector.get(ToastService);
    }

    if (!this.dialogService) {
      this.dialogService = this.injector.get(DialogService);
    }

    // Mostriamo i toast di errore solo se l'errore non è "user facing o di tipo validation",
    // perché in caso sia user facing o validation, l'errore è già stato gestito e mostrato all'utente.
    if (!isUserFacingOrValidationError) {
      if (error.errorPresentation) {
        this.handleErrorPresentation(error.errorPresentation);
      } else {
        this.toastService.showError(error.message);
      }
    }

    if (error instanceof AppError) {
      this.errorService.logError(error);
    }
  }

  /**
   * Gestisce la presentazione dell'errore
   */
  private handleErrorPresentation(errorPresentation: ErrorPresentation): void {
    switch (errorPresentation.type) {
      case 'dialog':
        this.dialogService.showDanger({
          content: errorPresentation.errorMessage,
          cancelButtonText: undefined,
          confirmButtonText: ERROR_DIALOG_TRANSLATIONS.closeButtonText,
        });
        break;

      case 'toast':
        this.toastService.showError(errorPresentation.errorMessage);
        break;
    }
  }
}
